import { useSelector } from 'react-redux';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, Box, Stack, Link } from '@chakra-ui/react';
import { useQuery } from 'hooks/useCustomQuery';
import { axios } from 'services/axios';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import { useState } from 'react';
import FormBase from '@/components/forms/new/FormBase';
import LoadingIndicator from 'components/v4/LoadingIndicator';
import InternationalRateNameField from './formFields/InternationalRateNameField';
import InternationalRateStatusField from './formFields/InternationalRateStatusField';
import InternationalRateWholesalerField from './formFields/InternationalRateWholesalerField';
import InternationalRatePartnerField from './formFields/InternationalRatePartnerField';
import { breadCrumbStyling } from 'components/forms/new/formStyles';



export default function InternationalRateCreate({ setInternationalRatesAction, onCreated }) {
    const [error, setError] = useState(null);
    const [selectedWholesalerID, setSelectedWholesalerID] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    // Read user's logged-in scope directly from Redux state
    // Use requiredScope (user's actual scope) not currentScope (which syncs with dropdown)
    const { requiredScope } = useSelector((state) => state.login);
    // Read user's actual wholesaler/partner from their login context, not from scope dropdown
    const { currentWholesaler, currentPartner } = useSelector((state) => state.navigationLists);
    
    // Determine initial values based on user's logged-in scope (requiredScope)
    // Global (80): Empty, user must choose
    // Wholesaler (60): Use user's actual wholesaler, partner can be chosen
    // Partner (40): Use user's actual wholesaler and partner
    const initialValues = {
        name: '',
        isPublic: false,
        isActive: true,
        wholesalerID: requiredScope === 80 ? '' : (currentWholesaler || ''),
        partnerID: requiredScope === 40 ? (currentPartner || '') : '',
    };
    const { data: wholesalerOptions = [] } = useQuery({
        queryKey: ['intlRateWholesalers', requiredScope],
        queryFn: async () => {
            const wholesalerRes = await axios.get('/wholesalers/');
            return (wholesalerRes.data || []).map((w) => ({
                label: w.name,
                value: w.id,
            }));
        },
        enabled: requiredScope === 80,
    });

    const partnerWholesalerID =
        requiredScope === 60 ? currentWholesaler : selectedWholesalerID;

    const { data: partnerOptions = [] } = useQuery({
        queryKey: ['intlRatePartners', requiredScope, partnerWholesalerID],
        queryFn: async () => {
            const partnerRes = await axios.get(`/partners/${partnerWholesalerID}`);
            return (partnerRes.data || []).map((p) => ({
                label: p.name,
                value: p.id,
            }));
        },
        enabled: Boolean(partnerWholesalerID) && (requiredScope === 80 || requiredScope === 60),
    });

    const handleWholesalerChange = async (form, wholesalerID) => {
        form.setFieldValue('wholesalerID', wholesalerID);
        form.setFieldValue('partnerID', null);
        setSelectedWholesalerID(wholesalerID || '');
    };

    const finalFormSubmit = async (values, actions) => {
        setIsLoading(true);
        setError(null);
        try {
            await axios.post('/internationalratelist', values);
            setInternationalRatesAction('Get');
        } catch (e) {
            setError('Failed to create International Rate List');
        } finally {
            setIsLoading(false);
            actions.setSubmitting(false);
        }
    };

    if (!wholesalerOptions) return <LoadingIndicator />;

    const breadCrumbTrail = [
        { label: 'International Rate Lists', path: '/billing/internationalRates' },
        { label: 'Create', path: '' },
    ];



    return (
        <>
              <Breadcrumb sx={breadCrumbStyling}>
                         {breadCrumbTrail?.map((crumb, index) => (
                             <BreadcrumbItem key={index}>
                                 {Array.isArray(crumb.path) ? (
                                     <BreadcrumbLink
                                         as={Link}
                                         to={crumb.path[0]}
                                         state={crumb.path[1]}
                                         onClick={index === 0 ? () => setInternationalRatesAction('Get') : undefined}
                                     >
                                         {crumb.label}
                                     </BreadcrumbLink>
                                 ) : (
                                     <BreadcrumbLink
                                         as={Link}
                                         to={crumb.path}
                                         onClick={index === 0 ? () => setInternationalRatesAction ('Get') : undefined}
                                     >
                                         {crumb.label}
                                     </BreadcrumbLink>
                                 )}
                             </BreadcrumbItem>
                         ))}
                     </Breadcrumb>
            <FormBase
             
                initialValues={initialValues}
                finalFormSubmit={finalFormSubmit}
                ufBackBtn="back-btn"
                ufSaveBtn="save-btn"
                  customClick={true}
                onBack={() => setInternationalRatesAction('Get')}
                isLoading={isLoading}
            >
                {({ errors = {}, touched = {}, values = {}, setFieldValue }) => [
                        <InternationalRateNameField 
                            key="name"
                            errors={errors} 
                            touched={touched} 
                            values={values} 
                            setFieldValue={setFieldValue} 
                        />,
                        <InternationalRateStatusField 
                            key="status"
                            errors={errors} 
                            touched={touched} 
                            values={values} 
                            setFieldValue={setFieldValue} 
                        />,
                        // Only show wholesaler field for Global (80) users
                        requiredScope === 80 && (
                            <InternationalRateWholesalerField
                                key="wholesaler"
                                errors={errors}
                                touched={touched}
                                values={values}
                                setFieldValue={setFieldValue}
                                wholesalerOptions={wholesalerOptions}
                                isLoading={isLoading}
                                darkMode={darkMode}
                                onChange={handleWholesalerChange}
                            />
                        ),
                        // Show partner field for Global (80) and Wholesaler (60) users
                        (requiredScope === 80 || requiredScope === 60) && (
                            <InternationalRatePartnerField
                                key="partner"
                                errors={errors}
                                touched={touched}
                                darkMode={darkMode}
                                setFieldValue={setFieldValue}
                                partnerOptions={partnerOptions}
                                isLoading={isLoading}
                            />
                        ),
                        error && (
                            <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                                <AlertIcon />
                                <AlertDescription>{error}</AlertDescription>
                            </Alert>
                        )
                ].filter(Boolean)}
            </FormBase>
        </>
    );
}